<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Translate Price Tiers" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-0">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-4">
          <div>
            <h5 class="mb-1 fs-15">Pricing Tiers</h5>
            <p class="text-muted fs-13 mb-0">Manage page-range based pricing for the Translate service.</p>
          </div>
          <RouterLink to="/translate-price-tiers/create" class="btn btn-outline-primary py-1 px-2 px-sm-4 fs-14 fw-medium rounded-3 hover-bg">
            <span class="py-sm-1 d-block">
              <i class="ri-add-line d-none d-sm-inline-block me-1"></i>
              <span>Add New Tier</span>
            </span>
          </RouterLink>
        </div>

        <div class="default-table-area style-two default-table-width">
          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Label</th>
                  <th scope="col">Min Pages</th>
                  <th scope="col">Max Pages</th>
                  <th scope="col">Price</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="7" class="text-center py-4">Loading...</td>
                </tr>
                <tr v-else-if="tiers.length === 0">
                  <td colspan="7" class="text-center py-4">No pricing tiers found.</td>
                </tr>
                <tr v-else v-for="tier in tiers" :key="tier.id">
                  <td>#{{ tier.id }}</td>
                  <td class="fw-medium text-dark">{{ tier.label || '—' }}</td>
                  <td>{{ tier.min_pages }}</td>
                  <td>{{ tier.max_pages ?? '∞' }}</td>
                  <td class="fw-semibold text-primary">{{ formatPrice(tier.price) }}</td>
                  <td>
                    <span class="badge p-2 fs-12 fw-normal" :class="tier.is_active ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'">
                      {{ tier.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td>
                    <div class="d-flex align-items-center gap-1">
                      <RouterLink :to="`/translate-price-tiers/${tier.id}/edit`" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-body">edit</i>
                      </RouterLink>
                      <button @click="deleteTier(tier.id)" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-danger">delete</i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import PageTitle from '@/components/Common/PageTitle.vue';
import translatePriceTierService from '@/services/translatePriceTierService';

export default defineComponent({
  name: 'TranslatePriceTierListPage',
  components: { PageTitle },
  setup() {
    const tiers = ref<any[]>([]);
    const loading = ref(true);

    const fetchTiers = async () => {
      loading.value = true;
      try {
        const response = await translatePriceTierService.getAll();
        tiers.value = response.data;
      } catch (error) {
        console.error('Error fetching tiers:', error);
      } finally {
        loading.value = false;
      }
    };

    const deleteTier = async (id: number) => {
      if (confirm('Are you sure you want to delete this pricing tier?')) {
        try {
          await translatePriceTierService.delete(id);
          fetchTiers();
        } catch (error) {
          console.error('Error deleting tier:', error);
        }
      }
    };

    const formatPrice = (price: string | number) => {
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0
      }).format(Number(price));
    };

    onMounted(fetchTiers);

    return { tiers, loading, deleteTier, formatPrice };
  }
});
</script>
